import { useEffect } from "react";
import { Link } from "react-router-dom";

const LEGAL_NAME = "JNA Ventures LLC";
const DBA = "Aminoplex";
const ADDRESS = "P.O. Box 491, Haymarket, VA 20168";
const CONTACT_EMAIL = "support@aminoplex.bio";
const EFFECTIVE = "October 1, 2025";

const DOCS = {
    terms: {
        title: "Terms & Conditions",
        body: `## Effective ${EFFECTIVE}

These Terms & Conditions govern all use of aminoplex.bio ("Site"), operated by ${LEGAL_NAME} d/b/a ${DBA} ("Aminoplex", "we", "us"). By accessing the Site or purchasing a product, you agree to these Terms.

## 1. Research Use Only
All products offered on this Site are supplied strictly for **in-vitro laboratory research use by qualified researchers, professionals, and institutions**. Products are **NOT** for human or animal consumption, medical use, therapeutic use, diagnostic use, cosmetic use, or use in any food, drug, dietary supplement, or household product. Products are not drugs, foods, cosmetics, or dietary supplements as defined by the U.S. FDA.

## 2. Qualified Purchaser Requirement
To create an account or place an order, you must:
- Be at least **21 years of age**.
- Provide your legal name, valid contact information, and a verifiable research affiliation (business, laboratory, university, or organization).
- Identify your buyer category and field of qualified research.
- Attest, electronically, that products will be used **exclusively for legitimate laboratory research**.

We reserve the right to review, request additional verification, delay, or refuse any account or order at our sole discretion.

## 3. No Guest Checkout
Purchases may only be completed through a verified, logged-in account that has electronically accepted these Terms and the Research-Use-Only attestation. Any attempted circumvention (guest checkout, one-click checkout, or accelerated payment methods) is not permitted.

## 4. Prohibited Use
You agree not to (a) consume, ingest, inhale, inject, or otherwise administer any product to a human or animal; (b) resell, gift, or transfer any product to any party for the purpose of human or animal consumption; or (c) misrepresent your qualification, affiliation, or purpose.

## 5. Order Review & Refusal
We may hold, review, cancel, or refuse any order that appears (in our sole judgment) to be intended for non-research use, contain inconsistent buyer information, or violate any law or these Terms. A canceled or refused order will be refunded in full to the original payment method.

## 5a. Prohibited Buyer Categories
We do **not** sell to buyers whose end-use is not qualified in-vitro laboratory research. Accounts and orders associated with the following categories will be refused: retail pharmacies, compounding pharmacies, med spas, weight-loss clinics, TRT/hormone clinics, wellness clinics, gyms, personal-training studios, dormitories, and any purchaser who indicates the products will be resold to consumers, administered to humans or animals, or repackaged as a drug, supplement, cosmetic, or food.

## 6. Pricing, Taxes, Shipping
Prices are listed in U.S. dollars and may change without notice. Applicable taxes are calculated at checkout. Shipping policy is set out on our /shipping page and incorporated here.

## 7. Intellectual Property
All Site content, branding, and product photography are the property of ${LEGAL_NAME}. You may not copy, distribute, or use our materials without written consent.

## 8. Limitation of Liability
Products are supplied "AS IS" for research purposes. To the maximum extent permitted by law, ${LEGAL_NAME} disclaims all warranties, express or implied, and shall not be liable for any damages arising out of misuse, non-research use, or any personal-use application of any product. You assume full responsibility for lawful handling, storage, disposal, and use of any product supplied.

## 9. Indemnification
You agree to indemnify and hold harmless ${LEGAL_NAME}, its officers, employees, and affiliates from any claim, damage, or expense arising from your use of products in violation of these Terms or applicable law.

## 10. Governing Law
These Terms are governed by the laws of the Commonwealth of Virginia, without regard to conflict-of-law principles. Any dispute shall be resolved exclusively in the state or federal courts located in Prince William County, Virginia.

## 11. Changes
We may update these Terms at any time by posting a revised version on the Site. Continued use after posting constitutes acceptance.

## 12. Contact
${LEGAL_NAME} d/b/a ${DBA}  
${ADDRESS}  
Email: ${CONTACT_EMAIL}`,
    },
    privacy: {
        title: "Privacy Policy",
        body: `## Effective ${EFFECTIVE}

${LEGAL_NAME} d/b/a ${DBA} ("we") respects your privacy. This Policy explains what personal information we collect and how we use it.

## Information We Collect
- **Account information:** legal name, email, phone, business/laboratory affiliation, buyer category, and field of qualified research.
- **Order information:** billing and shipping addresses, order history, and payment status. We do NOT store full payment-card numbers on our servers; card processing is handled by our PCI-DSS-compliant payment provider.
- **Technical data:** IP address, browser type, and interaction logs (used for security and abuse prevention).

## How We Use It
- To verify your qualification as a research purchaser.
- To fulfill orders and communicate order/shipping status.
- To respond to support requests.
- To comply with legal obligations and prevent fraud or misuse.

## Sharing
We do not sell or rent personal information. We share data only with:
- The payment processor that handles your card or Zelle transaction.
- Shipping carriers (name, address, phone) strictly for delivery.
- Law enforcement or regulators when legally required.

## Retention
We retain account and order records for a minimum of seven (7) years for tax and compliance purposes.

## Your Rights
You may request access to, correction of, or deletion of your personal data by emailing ${CONTACT_EMAIL}. Deletion of account data does not remove records we are legally required to retain.

## Security
We use encrypted (HTTPS) connections site-wide, hashed passwords, and access-controlled databases. No system is 100% secure; you are responsible for keeping your account password confidential.

## Contact
${CONTACT_EMAIL}`,
    },
    shipping: {
        title: "Shipping Policy",
        body: `## Effective ${EFFECTIVE}

## Processing Time
Orders are processed within **1–2 business days** after payment is confirmed and the account passes qualification review.

## Shipping Methods & Costs
- **Flat Rate Priority Shipping — $14.99** (waived on orders $200+).
- Domestic U.S. shipping only.
- Tracking information is emailed once the order is dispatched.

## Delivery Estimate
Typical U.S. delivery is 2–5 business days after dispatch. Delivery time is not guaranteed.

## Signature & Handling
Some orders may require an adult signature (21+) at delivery. We are not responsible for orders left unattended at unsecured addresses.

## Address Accuracy
You are responsible for providing an accurate shipping address. Orders returned to us due to an invalid address will be re-shipped at the customer's expense.

## Lost or Damaged Packages
Report lost or damaged shipments to ${CONTACT_EMAIL} within 7 days of the expected delivery date. We will work with the carrier to resolve the claim.

## International
We do not currently ship internationally.`,
    },
    refund: {
        title: "Refund & Return Policy",
        body: `## Effective ${EFFECTIVE}

Because our products are supplied strictly for laboratory research use and cannot be resold once shipped, we do not accept returns of opened or unsealed vials.

## Eligibility for Refund
A full refund will be issued when:
- The order is canceled prior to shipment.
- The order is refused by our qualification review team.
- The product arrives damaged (photo evidence required within 7 days).
- The wrong product was shipped.

## Not Eligible
- Buyer's remorse.
- "It didn't work" (products are not sold with any efficacy claim of any kind).
- Delays caused by the shipping carrier.
- Any product opened, punctured, or reconstituted.

## How to Request (RMA Process)
1. **Email ${CONTACT_EMAIL} within 7 calendar days** of delivery. Include your order number, the reason for the return, and — for damaged or incorrect items — photographs of the outer box, packing slip, and the affected vial(s) in their original condition.
2. **Wait for an RMA number.** Do not ship anything back before you have received a written RMA (Return Merchandise Authorization) number from our support team. Unauthorized returns will be refused at the receiving dock.
3. **Ship to our authorized returns address** (provided with the RMA email) using a tracked carrier. Return shipping is prepaid by the buyer unless the return is our error. All returns must reach us within 14 days of the RMA number being issued or the authorization expires.
4. **Refund issued** to the original payment method within 5–10 business days of receipt and inspection.

## Chargebacks
Please contact us first — we resolve nearly all disputes directly. Chargebacks filed without first contacting ${CONTACT_EMAIL} in good faith will be defended with the full order history: qualification data, IP, delivery signature, and communication logs.

## Correspondence Address
${LEGAL_NAME} d/b/a ${DBA} · P.O. Box 491, Haymarket, VA 20168, USA`,
    },
    compliance: {
        title: "Research-Use-Only Compliance Policy",
        body: `## Effective ${EFFECTIVE}

${LEGAL_NAME} d/b/a ${DBA} supplies research chemicals **exclusively** for **in-vitro laboratory research** conducted by qualified researchers, professionals, and institutions.

## Not for Human or Animal Use
Our products are:
- **NOT drugs.** They have not been evaluated by the U.S. Food and Drug Administration (FDA).
- **NOT intended to diagnose, cure, mitigate, treat, or prevent any disease.**
- **NOT dietary supplements, foods, cosmetics, or household products.**
- **NOT for use in humans or animals, whether for consumption, injection, inhalation, or topical application.**

## Buyer Qualification
Before creating an account or placing an order, every buyer must:
1. Confirm they are at least 21 years of age.
2. Provide their legal name and a valid business, laboratory, or institutional affiliation.
3. Select a buyer/researcher category and field of qualified research.
4. Electronically attest that products will be used exclusively for legitimate laboratory research and will not be consumed by humans or animals.
5. Electronically accept these Terms and this Compliance Policy.

## No Guest Checkout
We do not permit guest, one-click, or accelerated checkout that would bypass the buyer-qualification attestation.

## Order Review
Orders may be flagged and held for internal review based on unverified information, inconsistent shipping/billing details, high-risk patterns, or apparent intent for non-research use. Flagged orders will be released, delayed, or refused at our sole discretion.

## Customer Responsibility
You are solely responsible for the lawful handling, storage, disposal, and end-use of every product purchased. Misuse, resale for non-research use, or human/animal use is a material breach of your account and may result in account termination, order cancellation, and civil or criminal liability.

## Reporting Misuse
If you become aware of any misuse of a product supplied by ${DBA}, please report it immediately to ${CONTACT_EMAIL}.`,
    },
};

export default function PolicyPage({ doc }) {
    const d = DOCS[doc];
    useEffect(() => { window.scrollTo(0, 0); }, [doc]);
    if (!d) return null;
    return (
        <div data-testid={`policy-page-${doc}`} className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <div className="data-label text-brand mb-3">LEGAL</div>
                <h1 className="font-serif text-4xl md:text-5xl mb-8">{d.title}</h1>
                <article className="prose-legal">
                    {d.body.split(/\n\n/).map((block, i) => {
                        if (block.startsWith("## ")) {
                            return <h2 key={i} className="font-serif text-2xl text-primary mt-8 mb-3">{block.replace(/^##\s+/, "")}</h2>;
                        }
                        if (block.trim().startsWith("- ")) {
                            const items = block.split(/\n/).filter((l) => l.trim().startsWith("- "));
                            return (
                                <ul key={i} className="list-disc pl-6 space-y-1 text-secondary marker:text-brand mb-4">
                                    {items.map((it, j) => <li key={j}>{renderInline(it.replace(/^-\s+/, ""))}</li>)}
                                </ul>
                            );
                        }
                        return <p key={i} className="text-secondary leading-relaxed mb-4">{renderInline(block)}</p>;
                    })}
                </article>
                <div className="hairline my-12" />
                <div className="text-xs text-muted font-mono">
                    Related: <Link to="/terms" className="link-gold">Terms</Link> · <Link to="/privacy" className="link-gold">Privacy</Link> · <Link to="/shipping" className="link-gold">Shipping</Link> · <Link to="/refund" className="link-gold">Refunds</Link> · <Link to="/compliance" className="link-gold">RUO Compliance</Link>
                </div>
            </div>
        </div>
    );
}

function renderInline(s) {
    const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
        /^\*\*[^*]+\*\*$/.test(p)
            ? <strong key={i} className="text-primary font-medium">{p.slice(2, -2)}</strong>
            : <span key={i}>{p}</span>
    );
}
